import {InternalUserRole} from '@amplify/athena-models';
import Avatar from '@material-ui/core/Avatar';
import Collapse from '@material-ui/core/Collapse';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import {Theme, makeStyles} from '@material-ui/core/styles';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import {clsx} from 'clsx';
import {useSession} from 'next-auth/react';
import {KeyboardEvent, MouseEvent, SetStateAction, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {User} from '../Auth/User';
import Admin from '../Icons/Admin';
import AmplifyLogo from '../Icons/AmplifyLogo';
import AmplifyLogoSmall from '../Icons/AmplifyLogoSmall';
import Drill from '../Icons/Drill';
import File from '../Icons/File';
import Hide from '../Icons/Hide';
import Home from '../Icons/Home';
import Key from '../Icons/Key';
import LiveIcon from '../Icons/LiveIcon';
import Statistic from '../Icons/Statistic';
import UrlShortener from '../Icons/UrlShortener';
import Users from '../Icons/Users';
import constants from '../constants';

const useStyles = makeStyles((theme: Theme) => ({
    list: {
        overflowY: 'scroll',
        overflowX: 'hidden',
        maxHeight: 'unset',
        padding: theme.spacing(0, 3, 0, 2),
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '& $selected': {
            backgroundColor: '#C5CDF8',
        },
        '@media (max-height: 860px)': {
            maxHeight: 520,
        },
    },
    listClose: {
        padding: theme.spacing(0, 3, 0, 3),
        '& $menuItemText': {
            display: 'none',
        },
        '& $menuItem': {
            display: 'block',
            padding: '14px 20px',
        },
    },
    fullList: {
        width: 'auto',
    },
    menuHeader: {
        padding: theme.spacing(5, 3, 5, 4),
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        '& svg:last-of-type': {
            cursor: 'pointer',
        },
    },
    menuHeaderClose: {
        flexDirection: 'column',
        padding: theme.spacing(5, 3, 3, 5),
        alignItems: 'flex-start',
        '& svg:last-of-type': {
            transform: 'rotate(180deg)',
            marginTop: theme.spacing(3),
        },
    },
    logo: {
        marginRight: theme.spacing(7),
    },
    nestedList: {
        '& > div': {
            borderRadius: 6,
            '&:hover': {
                backgroundColor: '#3042A4',
            },
        },
        '& $menuItemText': {
            marginLeft: theme.spacing(5),
            '& span': {
                fontWeight: 400,
            },
        },
    },
    nestedListPaper: {
        transform: 'translateX(12px)',
        backgroundColor: '#3042A4',
        fontStyle: 'normal',
        fontWeight: 400,
        borderRadius: '0px 12px 12px 0px',
        fontSize: 18,
        lineHeight: '160%',
        color: '#C5CDF8',
        '& li': {
            padding: theme.spacing(1.5, 3),
            '&:first-child': {
                pointerEvents: 'none',
                fontWeight: 600,
                opacity: 1,
            },
        },
        '& ul': {
            padding: 0,
        },
        '& $selected': {
            backgroundColor: '#C5CDF8',
            color: '#3F51B5',
            '&:hover': {
                color: '#C5CDF8',
            },
        },
    },
    menuItemText: {
        '& span': {
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: 18,
            lineHeight: '160%',
            color: '#C5CDF8',
            transition: 'color .2s ease-out',
        },
    },
    menuItem: {
        '& div:first-child': {
            minWidth: 'unset',
            justifyContent: 'center',
            marginRight: theme.spacing(3),
        },
        borderRadius: 6,
        transition: 'background-color .2s ease-out',
        '&:last-of-type': {
            '& path': {
                fill: 'none',
                stroke: '#C5CDF8',
                strokeWidth: 2,
            },
        },
        '&:hover': {
            backgroundColor: '#3042A4',
        },
    },
    selected: {
        '& $menuItemText span': {
            color: '#3F51B5',
        },
        '& $icon path': {
            fill: '#3F51B5',
        },
        '&:hover': {
            '& $menuItemText span': {
                color: '#C5CDF8',
            },
            '& $icon path': {
                fill: '#C5CDF8',
            },
            '&:last-of-type': {
                '& path': {
                    fill: 'none',
                    stroke: '#C5CDF8',
                },
            },
        },
        '&:last-of-type': {
            '& path': {
                fill: 'none',
                stroke: '#3F51B5',
            },
        },
    },
    menuItemFullWidth: {
        width: 'fit-content',
        '& $icon': {
            marginRight: theme.spacing(0.5),
        },
    },
    menuItemHover: {
        backgroundColor: '#3042A4',
        borderRadius: '6px 0px 0px 6px',
    },
    icon: {
        '& path': {
            fill: '#C5CDF8',
        },
    },
    close: {
        fill: '#C5CDF8',
    },
    avatar: {
        cursor: 'pointer',
        margin: theme.spacing(5, 3, 5, 5),
    },
    drawer: {
        width: 320,
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    drawerOpen: {
        width: 320,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    paper: {
        background: '#3F51B5',
        borderRadius: '0px 16px 16px 0px',
        height: '100%',
        overflowY: 'hidden',
        justifyContent: 'space-between',
    },
    drawerClose: {
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: 111,
    },
}));

const {BUILD_COMMIT} = constants;
let commitHash = '';
if (BUILD_COMMIT) {
    commitHash = BUILD_COMMIT.substring(0, 7);
}

export default function AppNavMenu({
    state,
    onStateChanged,
    signOut,
}: {
    state: boolean;
    onStateChanged: (state: boolean) => void;
    signOut: () => void;
}) {
    const classes = useStyles();
    const [open, setOpen] = useState(true);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [anchorElClose, setAnchorElClose] = useState<null | HTMLElement>(null);
    const onAvatarClicked = (event: MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const session = useSession();
    const isAdmin = (session?.data as any)?.roles?.includes(InternalUserRole.AP_MANAGER);

    const handleCollapse = () => setOpen(!open);
    const handleMenu = (event: {currentTarget: SetStateAction<HTMLElement | null>}) => {
        if (!state) {
            if (anchorElClose !== event.currentTarget) {
                setAnchorElClose(event.currentTarget);
            }
        }
    };

    const toggleDrawer = (drawerOpen: boolean) => (event: KeyboardEvent | MouseEvent) => {
        if (
            event.type === 'keydown' &&
            ((event as KeyboardEvent).key === 'Tab' || (event as KeyboardEvent).key === 'Shift')
        ) {
            return;
        }
        onStateChanged(drawerOpen);
    };

    const history = useHistory();
    const [pathname, setPathname] = useState(history?.location?.pathname);

    const gotoPage = (appid: any) => {
        history.push(`/${appid}`);
        setPathname(`/${appid}`);
    };

    return (
        <div>
            <Drawer
                anchor="left"
                open={state}
                onClose={toggleDrawer(false)}
                variant="permanent"
                className={clsx(classes.drawer, {
                    [classes.drawerOpen]: state,
                    [classes.drawerClose]: !state,
                })}
                classes={{
                    paper: clsx(classes.paper, {
                        [classes.drawerOpen]: state,
                        [classes.drawerClose]: !state,
                    }),
                }}
            >
                <div role="presentation">
                    <div
                        className={clsx(classes.menuHeader, {
                            [classes.menuHeaderClose]: !state,
                        })}
                    >
                        {state && (
                            <>
                                <AmplifyLogo className={classes.logo} />
                                <Hide onClick={toggleDrawer(false)} />
                            </>
                        )}
                        {!state && (
                            <>
                                <AmplifyLogoSmall />
                                <Hide onClick={toggleDrawer(true)} />
                            </>
                        )}
                    </div>
                    <List
                        className={clsx(classes.list, {
                            [classes.listClose]: !state,
                        })}
                    >
                        <ListItem
                            button
                            key="home"
                            onClick={() => gotoPage('')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/'}
                        >
                            <ListItemIcon>
                                <Home className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText primary="Home" className={classes.menuItemText} />
                        </ListItem>
                        <ListItem
                            button
                            key="clients"
                            onClick={() => gotoPage('agent')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/agent'}
                        >
                            <ListItemIcon>
                                <Users className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText primary="Clients" className={classes.menuItemText} />
                        </ListItem>
                        <ListItem
                            button
                            key="applications"
                            onClick={() => gotoPage('case-manager')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/case-manager'}
                        >
                            <ListItemIcon>
                                <File className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText primary="Applications" className={classes.menuItemText} />
                        </ListItem>
                        <ListItem
                            button
                            key="analytics"
                            onClick={handleCollapse}
                            onMouseOver={handleMenu}
                            className={clsx(classes.menuItem, {
                                [classes.menuItemFullWidth]: !state,
                                [classes.menuItemHover]: !state && Boolean(anchorElClose),
                            })}
                        >
                            <ListItemIcon>
                                <Statistic className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText primary="Analytics" className={classes.menuItemText} />
                            {state &&
                                (open ? (
                                    <ExpandLess className={classes.close} />
                                ) : (
                                    <ExpandMore className={classes.close} />
                                ))}
                        </ListItem>
                        {state ? (
                            <Collapse in={open} timeout="auto" unmountOnExit>
                                <List disablePadding className={classes.nestedList}>
                                    <ListItem
                                        button
                                        key="walkthrough-analytics"
                                        onClick={() => gotoPage('analytics')}
                                        classes={{selected: classes.selected}}
                                        selected={pathname === '/analytics'}
                                    >
                                        <ListItemText
                                            primary="Walkthrough Analytics"
                                            className={classes.menuItemText}
                                        />
                                    </ListItem>
                                    <ListItem
                                        button
                                        key="widget-analytics"
                                        onClick={() => gotoPage('widget-analytics')}
                                        classes={{selected: classes.selected}}
                                        selected={pathname === '/widget-analytics'}
                                    >
                                        <ListItemText primary="Widget Analytics" className={classes.menuItemText} />
                                    </ListItem>
                                    <ListItem
                                        button
                                        key="advisor-ii-vul"
                                        onClick={() => gotoPage('advisor-ii-vul')}
                                        classes={{selected: classes.selected}}
                                        selected={pathname === '/advisor-ii-vul'}
                                    >
                                        <ListItemText primary="Advisor II VUL" className={classes.menuItemText} />
                                    </ListItem>
                                    <ListItem
                                        button
                                        key="client-data-analytics"
                                        onClick={() => gotoPage('client-data-analytics')}
                                        classes={{selected: classes.selected}}
                                        selected={pathname === '/client-data-analytics'}
                                    >
                                        <ListItemText primary="Client Data Report" className={classes.menuItemText} />
                                    </ListItem>
                                    <ListItem
                                        button
                                        key="daily-form-submission"
                                        onClick={() => gotoPage('daily-form-submission')}
                                        classes={{selected: classes.selected}}
                                        selected={pathname === '/daily-form-submission'}
                                    >
                                        <ListItemText
                                            primary="Daily Form Submission"
                                            className={classes.menuItemText}
                                        />
                                    </ListItem>
                                    <ListItem
                                        button
                                        key="data-feeds"
                                        onClick={() => gotoPage('data-feeds')}
                                        classes={{selected: classes.selected}}
                                        selected={pathname === '/data-feeds'}
                                    >
                                        <ListItemText primary="Data Feeds" className={classes.menuItemText} />
                                    </ListItem>
                                </List>
                            </Collapse>
                        ) : (
                            <Menu
                                anchorEl={anchorElClose}
                                open={Boolean(anchorElClose)}
                                onClose={() => setAnchorElClose(null)}
                                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                                elevation={0}
                                getContentAnchorEl={null}
                                classes={{paper: classes.nestedListPaper}}
                                MenuListProps={{onMouseLeave: () => setAnchorElClose(null)}}
                            >
                                <MenuItem key="analytics" disabled>
                                    Analytics
                                </MenuItem>
                                <MenuItem
                                    button
                                    key="walkthrough-analytics"
                                    onClick={() => gotoPage('analytics')}
                                    classes={{selected: classes.selected}}
                                    selected={pathname === '/analytics'}
                                >
                                    Walkthrough Analytics
                                </MenuItem>
                                <MenuItem
                                    button
                                    key="widget-analytics"
                                    onClick={() => gotoPage('widget-analytics')}
                                    classes={{selected: classes.selected}}
                                    selected={pathname === '/widget-analytics'}
                                >
                                    Widget Analytics
                                </MenuItem>
                                <MenuItem
                                    button
                                    key="advisor-ii-vul"
                                    onClick={() => gotoPage('advisor-ii-vul')}
                                    classes={{selected: classes.selected}}
                                    selected={pathname === '/advisor-ii-vul'}
                                >
                                    Advisor II VUL
                                </MenuItem>
                                <MenuItem
                                    button
                                    key="client-data-analytics"
                                    onClick={() => gotoPage('client-data-analytics')}
                                    classes={{selected: classes.selected}}
                                    selected={pathname === '/client-data-analytics'}
                                >
                                    Client Data Report
                                </MenuItem>
                                <MenuItem
                                    button
                                    key="daily-form-submission"
                                    onClick={() => gotoPage('daily-form-submission')}
                                    classes={{selected: classes.selected}}
                                    selected={pathname === '/daily-form-submission'}
                                >
                                    Daily Form Submission
                                </MenuItem>
                                <MenuItem
                                    button
                                    key="data-feeds"
                                    onClick={() => gotoPage('data-feeds')}
                                    classes={{selected: classes.selected}}
                                    selected={pathname === '/data-feeds'}
                                >
                                    Data Feeds
                                </MenuItem>
                            </Menu>
                        )}
                        <ListItem
                            button
                            key="tools"
                            onClick={() => gotoPage('tools')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/tools'}
                        >
                            <ListItemIcon>
                                <Drill className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText primary="External Tools" className={classes.menuItemText} />
                        </ListItem>
                        <ListItem
                            button
                            key="settings"
                            onClick={() => gotoPage('settings')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/settings'}
                        >
                            <ListItemIcon>
                                <Key className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText primary="Settings" className={classes.menuItemText} />
                        </ListItem>
                        {isAdmin && (
                            <ListItem
                                button
                                key="userAdmin"
                                onClick={() => gotoPage('user-admin')}
                                className={classes.menuItem}
                                classes={{selected: classes.selected}}
                                selected={pathname === '/user-admin'}
                            >
                                <ListItemIcon>
                                    <Admin className={classes.icon} />
                                </ListItemIcon>
                                <ListItemText primary="User Admin" className={classes.menuItemText} />
                            </ListItem>
                        )}
                        <ListItem
                            button
                            key="urlShortener"
                            onClick={() => gotoPage('url-shortener')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/url-shortener'}
                        >
                            <ListItemIcon>
                                <UrlShortener className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText primary="Url Shortener" className={classes.menuItemText} />
                        </ListItem>
                        <ListItem
                            button
                            key="live-call"
                            onClick={() => gotoPage('live-call')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/live-call'}
                        >
                            <ListItemIcon>
                                <LiveIcon className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText primary="Live Call" className={classes.menuItemText} />
                        </ListItem>
                    </List>
                </div>
                <Avatar className={classes.avatar} src={User.Info.image} onClick={onAvatarClicked} />
                <Menu
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                    getContentAnchorEl={null}
                >
                    <MenuItem>Version: {commitHash}</MenuItem>
                    <MenuItem onClick={signOut}>Logout</MenuItem>
                </Menu>
            </Drawer>
        </div>
    );
}
